import React, { type FC, useState, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { Pressable, xcss } from '@atlaskit/primitives';
import { MediaClientProvider } from '@atlaskit/media-client-react';
import type { MediaClientConfig } from '@atlaskit/media-core';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { MediaImage } from '@atlaskit/media-image';
import { token } from '@atlaskit/tokens';

import { withFlags, type FlagsStateContainer } from '@confluence/flags';
import { createEditorMediaProvider } from '@confluence/fabric-media-support';

import { PlaceholderThumbnail } from './PlaceholderThumbnail';

const baseStyles = xcss({
	height: '40px',
	width: '44px',
	borderRadius: 'border.radius.100',
	border: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	position: 'relative' as any,
	backgroundSize: 'cover',
	backgroundPosition: 'center',
	backgroundRepeat: 'no-repeat',
});

const selectedStyles = xcss({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	boxShadow: `inset 0 0 0 2px ${token('color.border.selected')}` as any,
});

const i18n = defineMessages({
	uploadImageFailedDescription: {
		id: 'custom-sites-extensions.shared-components.unsplash-search.upload.error.description',
		defaultMessage: 'Refresh the page and try again',
		description: `Description of flag shown when the extensions' uploaded image failed to load`,
	},
	uploadImageFailedTitle: {
		id: 'custom-sites-extensions.shared-components.unsplash-search.upload.error.title',
		defaultMessage: 'Image failed to load',
		description: `Title of flag shown when the extensions' uploaded image failed to load`,
	},
});

type UploadedImageThumbnailProps = {
	contentId: string;
	imageId: string;
	isSelected: boolean;
	isExternalShare: boolean;
	onClick: () => void;
};

const UploadedImageThumbnailComponent: FC<
	UploadedImageThumbnailProps & { flags: FlagsStateContainer }
> = ({ contentId, imageId, isSelected, isExternalShare, onClick, flags, children }) => {
	const { formatMessage } = useIntl();
	const [mediaConfig, setMediaConfig] = useState<{
		collection: string | undefined;
		authProvider: MediaClientConfig['authProvider'];
	} | null>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Setup media config
	useEffect(() => {
		const createMediaConfig = async () => {
			const { uploadParams, uploadMediaClientConfig } = await createEditorMediaProvider({
				contentId,
				contentType: 'page',
				isExternalShare,
			});

			if (uploadMediaClientConfig == undefined) {
				return null;
			}
			setMediaConfig({
				collection: uploadParams?.collection,
				authProvider: uploadMediaClientConfig.authProvider,
			});
		};

		void createMediaConfig();
	}, [contentId, isExternalShare]);

	if (!mediaConfig) {
		return <PlaceholderThumbnail />;
	}

	return (
		<MediaClientProvider clientConfig={mediaConfig}>
			<MediaImage
				identifier={{
					mediaItemType: 'file',
					id: imageId,
					collectionName: mediaConfig.collection,
				}}
				//force rerender image on id change until https://product-fabric.atlassian.net/browse/CXP-2899 is fixed
				key={imageId}
				mediaClientConfig={{
					authProvider: mediaConfig.authProvider,
				}}
				apiConfig={{
					allowAnimated: true,
					mode: 'full-fit' as const,
					upscale: false,
				}}
			>
				{({ data, loading, error }) => {
					if (loading || !data) {
						return <PlaceholderThumbnail />;
					}
					if (error) {
						void flags.showErrorFlag({
							id: 'upload-image-error',
							title: formatMessage(i18n.uploadImageFailedTitle),
							description: formatMessage(i18n.uploadImageFailedDescription),
						});

						createAnalyticsEvent({
							type: 'error',
							data: {
								actionSubject: 'image',
								actionSubjectId: 'coverImageSelected',
								source: 'coverImageSelectedComponent',
							},
						}).fire();
						return <PlaceholderThumbnail />;
					}
					return (
						<Pressable
							xcss={[baseStyles, isSelected && selectedStyles]}
							style={{
								backgroundImage: `url(${data.src})`,
							}}
							onClick={onClick}
							testId="uploaded-image-thumbnail"
						>
							{children}
						</Pressable>
					);
				}}
			</MediaImage>
		</MediaClientProvider>
	);
};

export const UploadedImageThumbnail = withFlags(UploadedImageThumbnailComponent);
